/**
 * One day of WorkMonitor against another: what changed, and by how much.
 *
 * The Employee Report already lists each day in a range on its own card, which
 * answers "what happened" a day at a time. It does not answer "is today going
 * better or worse than yesterday" — for that you have to read two cards and
 * subtract in your head, and nobody does that for eight people.
 *
 * Pure on purpose: no React, no api client. The arithmetic here is the part
 * worth being sure about, and it is testable on its own
 * (src/utils/dayComparison.test.ts).
 */

/** The fields of a system_level_tracking_summary row this comparison reads. */
export interface TrackingDay {
  date: string;
  total_tracked_mins: number;
  active_mins: number;
  idle_mins: number;
  first_activity: string | null;
  last_activity: string | null;
  app_usage?: { app_name: string; duration_mins: number }[] | null;
  /** Every block of the day, with the window title / URL / file path that
   *  tells you what was being worked on. Read by workAreas.ts. */
  timeline?: unknown[] | null;
  /** Added 2026-10-13. Absent on days summarised before the column existed. */
  tracking_sessions?: unknown[] | null;
  idle_periods?: unknown[] | null;
}

export interface MetricDelta {
  key: string;
  label: string;
  before: number;
  after: number;
  /** after - before. */
  delta: number;
  /** Percent change against `before`, or null when before is 0 — "up from
   *  nothing" has no percentage, and showing ∞ or 100% would both be lies. */
  percent: number | null;
  /** How to read this metric: "minutes", "percent" or "count". */
  unit: "minutes" | "percent" | "count";
  /** Whether a RISE is the good direction. Idle going up is not an improvement,
   *  so the colour cannot come from the sign alone. */
  higherIsBetter: boolean;
}

export interface AppDelta {
  app: string;
  before: number;
  after: number;
  delta: number;
}

export interface DayComparison {
  before: TrackingDay;
  after: TrackingDay;
  metrics: MetricDelta[];
  /** Apps sorted by the SIZE of the change, biggest first — what moved, not
   *  what is merely large. An app that ran 4h on both days changed nothing and
   *  is not news. */
  apps: AppDelta[];
}

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Active as a share of tracked, 0–100.
 *
 * 0 when nothing was tracked rather than NaN: a day with no tracking is 0%
 * efficient in every sense that matters here, and NaN would spread through
 * every delta that touched it.
 */
export function efficiency(day: TrackingDay): number {
  const tracked = num(day.total_tracked_mins);
  if (tracked <= 0) return 0;
  return Math.round((num(day.active_mins) / tracked) * 1000) / 10;
}

function delta(
  key: string, label: string, before: number, after: number,
  unit: MetricDelta["unit"], higherIsBetter: boolean,
): MetricDelta {
  const diff = Math.round((after - before) * 100) / 100;
  return {
    key, label, before, after, delta: diff, unit, higherIsBetter,
    percent: before === 0 ? null : Math.round((diff / before) * 1000) / 10,
  };
}

/** Minutes per app, lower-cased keys merged — "Chrome" and "chrome" are one
 *  app, and a day that reports both would otherwise show a phantom change. */
function appMinutes(day: TrackingDay): Map<string, { name: string; mins: number }> {
  const out = new Map<string, { name: string; mins: number }>();
  for (const row of day.app_usage || []) {
    const name = (row?.app_name || "").trim();
    if (!name) continue;
    const key = name.toLowerCase();
    const prev = out.get(key);
    out.set(key, {
      name: prev?.name || name,
      mins: (prev?.mins || 0) + num(row.duration_mins),
    });
  }
  return out;
}

/**
 * Compare two days. `before` is the earlier one — the caller orders them, and
 * passing them the wrong way round simply inverts every sign.
 */
export function compareDays(before: TrackingDay, after: TrackingDay): DayComparison {
  const metrics: MetricDelta[] = [
    delta("tracked", "Total tracked", num(before.total_tracked_mins), num(after.total_tracked_mins), "minutes", true),
    delta("active", "Active", num(before.active_mins), num(after.active_mins), "minutes", true),
    // More idle is not an improvement, whatever the sign says.
    delta("idle", "Idle", num(before.idle_mins), num(after.idle_mins), "minutes", false),
    delta("efficiency", "Efficiency", efficiency(before), efficiency(after), "percent", true),
    // Punches are neither good nor bad — a day split in three is just a day
    // split in three. Flagged as "higher is better" would colour a lunch break
    // green; the UI leaves count metrics neutral.
    delta("punches", "Punches", (before.tracking_sessions || []).length, (after.tracking_sessions || []).length, "count", true),
    delta("idlePeriods", "Idle periods", (before.idle_periods || []).length, (after.idle_periods || []).length, "count", false),
  ];

  const a = appMinutes(before);
  const b = appMinutes(after);
  const apps: AppDelta[] = [];
  for (const key of new Set([...Array.from(a.keys()), ...Array.from(b.keys())])) {
    const beforeMins = Math.round((a.get(key)?.mins || 0) * 100) / 100;
    const afterMins = Math.round((b.get(key)?.mins || 0) * 100) / 100;
    if (beforeMins === 0 && afterMins === 0) continue;
    apps.push({
      app: b.get(key)?.name || a.get(key)?.name || key,
      before: beforeMins,
      after: afterMins,
      delta: Math.round((afterMins - beforeMins) * 100) / 100,
    });
  }
  // Biggest movement first, either direction. Ties broken by name so the order
  // is stable between renders instead of following Map insertion.
  apps.sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta) || x.app.localeCompare(y.app));

  return { before, after, metrics, apps };
}

/**
 * The two days to compare out of whatever the range returned.
 *
 * The earliest and the latest, because that is the question the date pickers
 * are asking: "this day against that one". A range with one day has nothing to
 * compare and returns null rather than comparing a day with itself, which
 * would show a wall of zeroes and look like a bug.
 */
export function pickComparisonDays(
  days: TrackingDay[],
): { before: TrackingDay; after: TrackingDay } | null {
  const usable = (days || []).filter((d) => d && d.date);
  if (usable.length < 2) return null;
  const sorted = [...usable].sort((a, b) => a.date.localeCompare(b.date));
  return { before: sorted[0], after: sorted[sorted.length - 1] };
}
